import { DEFAULT_COLUMNS, getColSorter, type ColSorter, type ColSource } from "@/lib/columns";
import { isPeriodColumnKey } from "@/lib/period-columns";
import { isExternalColumnKey, parseExternalColumnKey } from "@/lib/external-sources";
import { resolveColumnValue, type ResolvedColumnValue } from "@/lib/column-value-resolver";
import { isActionDiaryDayColumnKey } from "@/lib/action-diary-columns";
import { isPromoActionsMarkerKey } from "@/lib/promo-actions-columns";
import { isWeekCalendarColumnKey, weekCalendarOfColumnKey } from "@/lib/week-calendars";
import { CLUSTER_DELIVERY_DAYS_METRIC } from "@/lib/delivery-days";
import type { ProductOrderDailyMetricFields } from "@/lib/product-order-daily";
import { dataKey, type Product } from "@/lib/product-types";
import type { SortDeps } from "@/lib/sort-types";

export type DataDemandSource =
  | "productsLite"
  | "productsFull"
  | "orders"
  | "attrs"
  | "prices"
  | "ads"
  | "clusters"
  | "inventory"
  | "costs"
  | "suppliers"
  | "topQueries"
  | "productGroups"
  | "clusterStockHistory"
  | "clusterOrderFlows"
  | "contentRating"
  | "turnoverHistory"
  | "batches"
  | "supplies"
  | "finances"
  | "promos"
  | "wb"
  | "actionDiary"
  /** Ряды по дням для календарей таблицы (lib/week-calendars): остатки, цена, реклама. */
  | "stockDays"
  | "priceDays"
  | "adsDays"
  /** Сроки доставки по кластерам (lib/delivery-days) для `cluster_delivery_days:*`. */
  | "deliveryDays"
  /** Деньги по товару за 7/14/30 дней (lib/finance-product-money) для `money_*`. */
  | "productMoney";

const colSourceByKey = new Map(DEFAULT_COLUMNS.map(col => [col.key, col.source]));
const colDefByKey = new Map(DEFAULT_COLUMNS.map(col => [col.key, col]));
const registryEntryCache = new Map<string, ColumnRegistryEntry>();

const LITE_PRODUCT_COLUMN_KEYS = new Set([
  "photo",
  "name",
  "offer_id",
  "product_id",
  "status",
  "price",
  "old_price",
  "stock",
  "reserved",
]);

export const DAILY_ORDER_FULL_SERIES_COLUMN_KEYS = new Set<string>([
  "orders_daily",
  "orders_sparkline",
  "customer_price_chart",
]);

export const DAILY_ORDER_SCALAR_COLUMN_KEYS = new Set<string>([
  "orders_1d",
  "orders_7d",
  "orders_14d",
  "orders_30d",
  "orders_revenue",
  "customer_price_avg_7d",
  "orders_speed",
  "orders_speed_14d",
  "orders_speed_30d",
  "orders_clusters",
  "orders_clusters_7d",
  "orders_clusters_14d",
  "orders_clusters_speed_7d",
  "orders_clusters_speed_14d",
  "orders_clusters_speed_30d",
  "abc_orders_7d",
  "abc_orders_14d",
  "abc_orders_30d",
  "cluster_days_left_7d",
  "cluster_days_left_14d",
  "cluster_days_left_30d",
  "ads",
  "idc",
  "turnover",
]);

export const TABLE2_DAILY_ORDER_SQL_COLUMN_KEYS = new Set<string>([
  ...DAILY_ORDER_SCALAR_COLUMN_KEYS,
  ...DAILY_ORDER_FULL_SERIES_COLUMN_KEYS,
]);

export const VIEW_ROWS_DAILY_ORDER_SQL_COLUMN_KEYS = new Set<string>(DAILY_ORDER_SCALAR_COLUMN_KEYS);

/**
 * Колонки, чьи значения резолвятся из product.analytics (источник Ozon
 * v1/analytics/data): заказы-шт, показы, конверсии, сессии и их графики/тренды.
 * Аналитика подмешивается к товарам отдельной опцией includeDailyAnalytics
 * (readProductsCompatMerged) — если запрошена такая колонка, а опция не
 * включена, резолвер молча вернёт 0 по всем строкам (юзер поймал: виджет
 * «заказы по типам товара» с метрикой ordered_units показывал «нет данных»).
 */
export function columnNeedsDailyAnalytics(colKey: string): boolean {
  return colSourceByKey.get(colKey) === "v1/analytics/data";
}

export function isKnownColumnKey(colKey: string): boolean {
  return colSourceByKey.has(colKey)
    || isActionDiaryDayColumnKey(colKey)
    || isWeekCalendarColumnKey(colKey)
    || colKey.startsWith("attr_")
    || colKey.startsWith("promo_")
    || colKey.startsWith("cluster_")
    || colKey.startsWith("wb_region_")
    || /^ad_.+_(7d|14d|30d)$/.test(colKey)
    || /^money_type_\d+_(7d|14d|30d)$/.test(colKey)
    || colKey.startsWith("custom_number:")
    // «За период»: данные грузит сам блок таблицы по своему календарю
    // (app/workspace/useFlowTablePeriod.tsx), общих источников не требует.
    || isPeriodColumnKey(colKey)
    // Колонки из своих таблиц (lib/external-sources): значения грузит блок
    // таблицы (app/workspace/useFlowTableExternal.tsx).
    || isExternalColumnKey(colKey);
}

export type ColumnValueKind = "number" | "text" | "none";

export type ColumnRegistryEntry = {
  key: string;
  known: boolean;
  dataSources: DataDemandSource[];
  sorter?: ColSorter;
  sortKind: ColumnValueKind;
  filterKind: "number" | "text";
};

export type ColumnFilterValue = string | number | null;

function dynamicColumnSortKind(colKey: string): ColumnValueKind {
  if (colKey.startsWith("custom_number:")) return "number";
  if (isPeriodColumnKey(colKey)) return "number";
  // Тип колонки из своей таблицы зашит в ключ: число — по числу, остальное — текст.
  const external = parseExternalColumnKey(colKey);
  if (external) return external.type === "number" ? "number" : "text";
  // Дни и недели календарей — числа; сам маркер разворачивается и не сортируется.
  if (isWeekCalendarColumnKey(colKey)) return "number";
  if (colKey.startsWith("cluster_")) return "number";
  if (colKey.startsWith("wb_region_")) return "number";
  // Маркер «Акции» сам по себе не сортируется (он разворачивается в колонки).
  // Колонки конкретных акций (promo_action:<id>) — сортируются по участию.
  if (isPromoActionsMarkerKey(colKey)) return "none";
  if (colKey.startsWith("promo_")) return "number";
  if (/^ad_.+_(7d|14d|30d)$/.test(colKey) || colKey === "ad_presence") return "number";
  return "none";
}

/**
 * Единая карточка колонки для технических сценариев: какие данные грузить,
 * как её сортировать и какой тип фильтра показывать. UI-описание колонок
 * остаётся в DEFAULT_COLUMNS, но потребители должны ходить сюда, чтобы не
 * плодить отдельные карты для sort/filter/data-needs.
 */
export function getColumnRegistryEntry(colKey: string): ColumnRegistryEntry {
  const cached = registryEntryCache.get(colKey);
  if (cached) return cached;
  const sorter = getColSorter(colKey);
  const sortKind: ColumnValueKind = sorter?.text ? "text" : sorter?.num ? "number" : dynamicColumnSortKind(colKey);
  const known = isKnownColumnKey(colKey);
  const entry = {
    key: colKey,
    known,
    dataSources: getColumnDataSources(colKey),
    sorter,
    sortKind,
    filterKind: columnFilterKindFromSortKind(colKey, sortKind),
  };
  registryEntryCache.set(colKey, entry);
  return entry;
}

export function getColumnDefinition(colKey: string) {
  return colDefByKey.get(colKey);
}

export function getColumnSorter(colKey: string): ColSorter | undefined {
  return getColumnRegistryEntry(colKey).sorter;
}

export function getColumnSortKind(colKey: string): ColumnValueKind {
  return getColumnRegistryEntry(colKey).sortKind;
}

function columnFilterKindFromSortKind(colKey: string, sortKind: ColumnValueKind): "number" | "text" {
  if (sortKind === "text") return "text";
  if (sortKind === "number") return "number";

  // Сохраняет старое поведение фильтров: неизвестные обычные колонки считались
  // текстовыми, а динамические cluster/promo/custom_number — числовыми.
  if (colKey.startsWith("cluster_") || colKey.startsWith("wb_region_") || colKey.startsWith("promo_") || colKey.startsWith("custom_number:")) return "number";
  return "text";
}

export function getColumnFilterKind(colKey: string): "number" | "text" {
  return getColumnRegistryEntry(colKey).filterKind;
}

export function resolveColumnRegistryValue(product: Product, colKey: string, deps: SortDeps): ResolvedColumnValue | null {
  const resolved = resolveColumnValue(product, colKey, deps);
  if (resolved) return resolved;

  const sorter = getColumnRegistryEntry(colKey).sorter;
  if (sorter?.text) {
    const value = sorter.text(product, deps);
    return { exportValue: value, textValue: value };
  }
  if (sorter?.num) {
    const value = sorter.num(product, deps);
    return { exportValue: value, sortValue: value };
  }

  return null;
}

export function getColumnRegistrySortString(product: Product, colKey: string, deps: SortDeps): string {
  const cache = deps.__valueCache;
  const cacheKey = cache ? columnValueCacheKey(product, colKey) : null;
  if (cache && cacheKey) {
    const cached = cache.text.get(cacheKey);
    if (cached !== undefined) return cached;
  }
  const value = resolveColumnRegistryValue(product, colKey, deps)?.textValue ?? "";
  if (cache && cacheKey) cache.text.set(cacheKey, value);
  return value;
}

export function getColumnRegistrySortValue(product: Product, colKey: string, deps: SortDeps): number {
  const cache = deps.__valueCache;
  const cacheKey = cache ? columnValueCacheKey(product, colKey) : null;
  if (cache && cacheKey) {
    const cached = cache.numeric.get(cacheKey);
    if (cached !== undefined) return cached;
  }
  const value = resolveColumnRegistryValue(product, colKey, deps)?.sortValue ?? 0;
  if (cache && cacheKey) cache.numeric.set(cacheKey, value);
  return value;
}

export function getColumnRegistryFilterValue(product: Product, colKey: string, deps: SortDeps): ColumnFilterValue {
  if (getColumnFilterKind(colKey) === "text") return getColumnRegistrySortString(product, colKey, deps);
  const value = getColumnRegistrySortValue(product, colKey, deps);
  return Number.isFinite(value) ? value : null;
}

export function getColumnRegistryExportValue(product: Product, colKey: string, deps: SortDeps): string | number | null {
  return resolveColumnRegistryValue(product, colKey, deps)?.exportValue ?? null;
}

function columnValueCacheKey(product: Product, colKey: string): string {
  return `${colKey}\u0001${dataKey(product)}\u0001${product.id}`;
}

function addByColSource(out: Set<DataDemandSource>, source: ColSource | undefined, colKey: string) {
  if (!source) return;
  switch (source) {
    case "v1/analytics/data":
      out.add("productsFull");
      break;
    case "v4/product/info/attributes":
      out.add("attrs");
      break;
    case "v5/product/info/prices":
      out.add("prices");
      break;
    case "performance_api":
      out.add("ads");
      break;
    case "orders_api":
      out.add("orders");
      break;
    case "supplies_api":
      out.add("supplies");
      break;
    case "finances_api":
      out.add("finances");
      break;
    case "stock_history_api":
      out.add("orders");
      out.add("supplies");
      out.add("finances");
      break;
    case "v1/actions/candidates":
      out.add("promos");
      break;
    case "v1/analytics/stocks":
      out.add("inventory");
      break;
    case "v3/product/info/list":
      if (!LITE_PRODUCT_COLUMN_KEYS.has(colKey)) out.add("productsFull");
      break;
    case "local":
      break;
    default: {
      // Новое значение ColSource нельзя добавить незаметно: TypeScript укажет
      // на эту строку, пока для источника не будет описана зависимость данных.
      const unhandledSource: never = source;
      return unhandledSource;
    }
  }
}

function addClusterNeeds(out: Set<DataDemandSource>, metric: string) {
  // Динамические колонки плана читают уже готовый результат расчёта блока,
  // а не общие Ozon-источники таблицы.
  if (metric.startsWith("cluster_plan_")) return;
  out.add("clusters");
  if (metric === "cluster_supply_request" || metric === "cluster_in_transit" || metric === "cluster_receiving") {
    out.add("supplies");
    return;
  }
  if (metric === "cluster_stock") {
    out.add("inventory");
    return;
  }
  // Срок доставки — своя выборка из истории отправлений и начислений.
  if (metric === CLUSTER_DELIVERY_DAYS_METRIC) {
    out.add("deliveryDays");
    return;
  }
  // Графики по кластеру: заказы и «откуда отгрузили» — из потоков заказов
  // (по настоящим датам), остаток в кластере — из истории по кластерам.
  if (metric === "cluster_orders_chart" || metric === "cluster_orders_stock_chart") {
    out.add("clusterOrderFlows");
    out.add("clusterStockHistory");
    // Окно дня ленты показывает и события дневника за день.
    if (metric === "cluster_orders_chart") out.add("actionDiary");
    return;
  }
  if (metric === "cluster_shipped_days_left") out.add("inventory");
  out.add("orders");
}

export function getColumnDataSources(colKey: string): DataDemandSource[] {
  const out = new Set<DataDemandSource>();

  if (!isKnownColumnKey(colKey)) return [];
  if (colKey.startsWith("custom_number:")) return [];
  if (isPeriodColumnKey(colKey)) return [];
  if (isExternalColumnKey(colKey)) return [];
  if (isActionDiaryDayColumnKey(colKey)) return ["actionDiary"];
  // Календари по неделям: свой ряд по дням, точки и окно дня — из дневника.
  const calendar = weekCalendarOfColumnKey(colKey);
  if (calendar) return [...calendar.sources];

  if (colKey.startsWith("attr_")) {
    out.add("attrs");
  } else if (colKey.startsWith("promo_")) {
    out.add("promos");
  } else if (/^ad_.+_(7d|14d|30d)$/.test(colKey) || colKey === "ad_presence") {
    out.add("ads");
  } else if (/^money_.+_(7d|14d|30d)$/.test(colKey)) {
    out.add("productMoney");
  } else if (colKey.startsWith("cluster_")) {
    const sep = colKey.indexOf(":");
    if (sep !== -1) addClusterNeeds(out, colKey.slice(0, sep));
  } else if (colKey.startsWith("wb_region_")) {
    out.add("wb");
  } else {
    addByColSource(out, colSourceByKey.get(colKey), colKey);
  }

  switch (colKey) {
    case "revenue":
      // Основное и точное значение — financeData. Полный товарный каталог был
      // нужен только для старого analytics-fallback, но одновременная загрузка
      // finances + productsFull удваивала товарный payload на каждой таблице с
      // выручкой. Если финансов нет, источник вернёт пустую карту и ячейка
      // честно покажет 0, не подмешивая другой смысл показателя.
      out.delete("productsFull");
      out.add("finances");
      break;
    case "promo_presence":
      out.add("promos");
      out.add("prices");
      break;
    case "cost_price":
      out.add("costs");
      break;
    case "supplier":
      out.add("suppliers");
      break;
    case "top_query":
      out.add("topQueries");
      break;
    case "product_group":
      out.add("productGroups");
      break;
    case "content_rating":
      out.add("contentRating");
      break;
    // Оборачиваемость показывает ещё и динамику: остаток с заказами неделю назад.
    case "turnover":
      out.add("turnoverHistory");
      break;
    case "batch_share":
      out.add("batches");
      break;
    // Деньги в остатке — остаток (уже есть в товаре) × себестоимость.
    case "stock_money":
      out.add("costs");
      break;
    // Видео/рич и полнота карточки живут в характеристиках товара.
    case "content_video":
    case "content_attrs":
      out.add("attrs");
      break;
    case "action_diary":
      out.add("actionDiary");
      break;
    // Окно дня ленты «Заказы по дням» показывает и события дневника за день.
    case "orders_daily":
      out.add("actionDiary");
      break;
    case "supply_order_in_transit":
    case "supply_cluster":
      out.add("clusters");
      break;
    case "stock_sparkline_30d":
    case "stock_orders_combo":
      out.add("orders");
      out.add("supplies");
      out.add("finances");
      break;
    case "orders_clusters":
    case "orders_clusters_speed_7d":
    case "orders_clusters_speed_14d":
    case "orders_clusters_speed_30d":
      out.add("orders");
      out.add("clusters");
      break;
    case "cluster_days_left_7d":
    case "cluster_days_left_14d":
    case "cluster_days_left_30d":
    // Матрица кластеров: заказы 7д по кластерам + остаток по складам кластера.
    case "cluster_matrix":
      out.add("orders");
      out.add("clusters");
      out.add("inventory");
      break;
    case "revenue_per_unit":
      // Выручка/заказ берёт finance-выручку (как колонка «Выручка» и резолвер);
      // analytics (ordered_units) уже добавлен по source. Без finances ячейка и
      // сортировка расходились: одна на finance, другая на analytics-фолбэке.
      out.add("finances");
      break;
    case "abc_revenue_7d":
    case "abc_revenue_14d":
    case "abc_revenue_30d":
      out.add("finances");
      break;
    case "abc_orders_7d":
    case "abc_orders_14d":
    case "abc_orders_30d":
      out.add("orders");
      break;
  }

  return [...out];
}

export function orderDailyMetricFieldsForKeys(keys: Set<string>): ProductOrderDailyMetricFields {
  return {
    qty1d: keys.has("orders_1d"),
    qty7d: [
      "orders_7d",
      "orders_speed",
      "orders_clusters_7d",
      "orders_clusters_speed_7d",
      "abc_orders_7d",
      "cluster_days_left_7d",
      "ads",
      "idc",
      "turnover",
    ].some(key => keys.has(key)),
    qty14d: [
      "orders_14d",
      "orders_speed_14d",
      "orders_clusters_14d",
      "orders_clusters_speed_14d",
      "abc_orders_14d",
      "cluster_days_left_14d",
    ].some(key => keys.has(key)),
    qty30d: [
      "orders_30d",
      "orders_clusters",
      "orders_clusters_speed_30d",
      "abc_orders_30d",
      "cluster_days_left_30d",
    ].some(key => keys.has(key)),
    revenue30d: keys.has("orders_revenue"),
    customerPrice7d: keys.has("customer_price_avg_7d"),
  };
}
